import { App, type BackButtonListenerEvent } from '@capacitor/app'
import {
  Capacitor,
  type PermissionState,
  type PluginListenerHandle,
  SystemBars,
  SystemBarsStyle,
} from '@capacitor/core'
import {
  PushNotifications,
  type ActionPerformed,
  type PushNotificationSchema,
  type RegistrationError,
  type Token,
} from '@capacitor/push-notifications'
import { SplashScreen } from '@capacitor/splash-screen'
import type { Router } from 'vue-router'
import { createLogger } from '@/@vxapp/@core/Logger/VxLog'
import { showNativeAlert, showNativeConfirm } from '@/native/nativeDialogs'

const log = createLogger('NativeApp')

/* eslint-disable no-unused-vars -- Core ESLint flags TypeScript enum and window event contract members. */
export enum NativeAppEventName {
  AppPaused = 'vxapp:native-app-paused',
  AppResumed = 'vxapp:native-app-resumed',
  BackButton = 'vxapp:native-back-button',
  PushNotificationOpened = 'vxapp:native-push-opened',
  PushNotificationReceived = 'vxapp:native-push-received',
  PushPermissionChanged = 'vxapp:native-push-permission-changed',
  PushRegistration = 'vxapp:native-push-registration',
  PushRegistrationError = 'vxapp:native-push-registration-error',
}

enum NativePushDataField {
  DeepLink = 'deepLink',
  Path = 'path',
  Route = 'route',
  Url = 'url',
}

enum NativePushChannel {
  General = 'vortex_general',
}

interface NativePushRegistrationDetail {
  platform: string
  token: string
}

interface NativePushRegistrationErrorDetail {
  message: string
}

interface NativePushPermissionDetail {
  receive: PermissionState
}

interface NativePushNotificationDetail {
  notification: PushNotificationSchema
}

interface NativeBackButtonDetail {
  canGoBack: boolean
}

interface NativeAppEventDetailMap {
  [NativeAppEventName.AppPaused]: Record<string, never>
  [NativeAppEventName.AppResumed]: Record<string, never>
  [NativeAppEventName.BackButton]: NativeBackButtonDetail
  [NativeAppEventName.PushNotificationOpened]: NativePushNotificationDetail
  [NativeAppEventName.PushNotificationReceived]: NativePushNotificationDetail
  [NativeAppEventName.PushPermissionChanged]: NativePushPermissionDetail
  [NativeAppEventName.PushRegistration]: NativePushRegistrationDetail
  [NativeAppEventName.PushRegistrationError]: NativePushRegistrationErrorDetail
}

declare global {
  interface WindowEventMap {
    [NativeAppEventName.AppPaused]: CustomEvent<
      NativeAppEventDetailMap[NativeAppEventName.AppPaused]
    >
    [NativeAppEventName.AppResumed]: CustomEvent<
      NativeAppEventDetailMap[NativeAppEventName.AppResumed]
    >
    [NativeAppEventName.BackButton]: CustomEvent<
      NativeAppEventDetailMap[NativeAppEventName.BackButton]
    >
    [NativeAppEventName.PushNotificationOpened]: CustomEvent<
      NativeAppEventDetailMap[NativeAppEventName.PushNotificationOpened]
    >
    [NativeAppEventName.PushNotificationReceived]: CustomEvent<
      NativeAppEventDetailMap[NativeAppEventName.PushNotificationReceived]
    >
    [NativeAppEventName.PushPermissionChanged]: CustomEvent<
      NativeAppEventDetailMap[NativeAppEventName.PushPermissionChanged]
    >
    [NativeAppEventName.PushRegistration]: CustomEvent<
      NativeAppEventDetailMap[NativeAppEventName.PushRegistration]
    >
    [NativeAppEventName.PushRegistrationError]: CustomEvent<
      NativeAppEventDetailMap[NativeAppEventName.PushRegistrationError]
    >
  }
}
/* eslint-enable no-unused-vars */

const APP_LINK_HOST = 'vortexenterprises.com.au'
const APP_LINK_PATH_PREFIX = '/app'
const CUSTOM_LINK_PROTOCOL = 'vortex:'
const CUSTOM_LINK_HOSTS = new Set(['app', 'open'])
const LOCAL_CAPACITOR_ORIGIN = 'https://localhost'
const MAXIMUM_ROUTE_LENGTH = 2048
const NATIVE_CATCH_ALL_ROUTE_NAMES = new Set(['$all', '$error'])
const ACTIVE_LAYOUT_OVERLAY_SELECTOR = '.layout-overlay.visible'

const ACTIVE_TEMPORARY_DRAWER_SCRIM_SELECTOR =
  '.v-navigation-drawer--temporary.v-navigation-drawer--active + .v-navigation-drawer__scrim'

const VUETIFY_ACTIVE_OVERLAY_SELECTOR = '.v-overlay.v-overlay--active:not(.v-snackbar)'

type NativeThemeName = 'dark' | 'light'

let initializationPromise: Promise<void> | null = null
let runtimeGeneration = 0
const listenerHandles: PluginListenerHandle[] = []

const dispatchNativeEvent = <EventName extends NativeAppEventName>(
  eventName: EventName,
  detail: NativeAppEventDetailMap[EventName]
): void => {
  window.dispatchEvent(new CustomEvent(eventName, { detail }))
}

const getErrorMessage = (error: unknown): string => {
  if (error instanceof Error) {
    return error.message
  }

  if (typeof error === 'string') {
    return error
  }

  return 'Unknown native runtime error'
}

const isRuntimeActive = (generation: number): boolean => generation === runtimeGeneration

const runNativeTask = async (
  taskName: string,
  task: () => Promise<unknown>,
  generation: number
): Promise<void> => {
  if (!isRuntimeActive(generation)) {
    return
  }

  try {
    await task()
  } catch (error: unknown) {
    log.error(`Native ${taskName} failed`, error)
  }
}

const hasControlCharacters = (value: string): boolean =>
  Array.from(value).some(character => {
    const characterCode = character.charCodeAt(0)

    return characterCode <= 31 || characterCode === 127
  })

const normalizeRelativeRoute = (route: string): string | null => {
  const trimmedRoute = route.trim()

  if (
    !trimmedRoute.startsWith('/') ||
    trimmedRoute.startsWith('//') ||
    trimmedRoute.includes('\\') ||
    trimmedRoute.length > MAXIMUM_ROUTE_LENGTH ||
    hasControlCharacters(trimmedRoute)
  ) {
    return null
  }

  const parsedRoute = new URL(trimmedRoute, LOCAL_CAPACITOR_ORIGIN)

  if (parsedRoute.origin !== LOCAL_CAPACITOR_ORIGIN) {
    return null
  }

  return `${parsedRoute.pathname}${parsedRoute.search}${parsedRoute.hash}`
}

export const resolveNativeRoute = (candidate: unknown): string | null => {
  if (typeof candidate !== 'string') {
    return null
  }

  const relativeRoute = normalizeRelativeRoute(candidate)
  if (relativeRoute) {
    return relativeRoute
  }

  let parsedUrl: URL
  try {
    parsedUrl = new URL(candidate)
  } catch {
    return null
  }

  if (parsedUrl.protocol === CUSTOM_LINK_PROTOCOL && CUSTOM_LINK_HOSTS.has(parsedUrl.hostname)) {
    const explicitRoute = parsedUrl.searchParams.get(NativePushDataField.Route)

    return normalizeRelativeRoute(
      explicitRoute ?? `${parsedUrl.pathname}${parsedUrl.search}${parsedUrl.hash}`
    )
  }

  if (parsedUrl.protocol === 'https:' && parsedUrl.hostname === APP_LINK_HOST) {
    if (
      parsedUrl.pathname !== APP_LINK_PATH_PREFIX &&
      !parsedUrl.pathname.startsWith(`${APP_LINK_PATH_PREFIX}/`)
    ) {
      return null
    }

    const routePath = parsedUrl.pathname.slice(APP_LINK_PATH_PREFIX.length) || '/'

    return normalizeRelativeRoute(`${routePath}${parsedUrl.search}${parsedUrl.hash}`)
  }

  if (parsedUrl.origin === LOCAL_CAPACITOR_ORIGIN) {
    return normalizeRelativeRoute(`${parsedUrl.pathname}${parsedUrl.search}${parsedUrl.hash}`)
  }

  return null
}

const resolvePushNotificationRoute = (notification: PushNotificationSchema): string | null => {
  const data =
    typeof notification.data === 'object' && notification.data !== null
      ? (notification.data as Record<string, unknown>)
      : {}

  const routeCandidates = [
    data[NativePushDataField.Route],
    data[NativePushDataField.Path],
    data[NativePushDataField.DeepLink],
    data[NativePushDataField.Url],
    notification.link,
  ]

  for (const candidate of routeCandidates) {
    const route = resolveNativeRoute(candidate)
    if (route) {
      return route
    }
  }

  return null
}

const navigateToNativeRoute = async (
  router: Router,
  routeCandidate: unknown,
  source: string
): Promise<boolean> => {
  const route = resolveNativeRoute(routeCandidate)
  if (!route) {
    log.warn('Rejected an unsafe or unsupported native route', { source })

    return false
  }

  await router.isReady()

  const resolvedRoute = router.resolve(route)

  const isCatchAllRoute = resolvedRoute.matched.some(routeRecord => {
    const routeName = routeRecord.name

    return typeof routeName === 'string' && NATIVE_CATCH_ALL_ROUTE_NAMES.has(routeName)
  })

  if (resolvedRoute.matched.length === 0 || isCatchAllRoute) {
    log.warn('Ignored a native route that does not exist', { source, route })

    return false
  }

  if (router.currentRoute.value.fullPath !== resolvedRoute.fullPath) {
    await router.push(resolvedRoute.fullPath)
  }

  return true
}

const registerListener = async (
  listenerName: string,
  generation: number,
  register: () => Promise<PluginListenerHandle>
): Promise<void> => {
  if (!isRuntimeActive(generation)) {
    return
  }

  try {
    const listenerHandle = await register()

    if (isRuntimeActive(generation)) {
      listenerHandles.push(listenerHandle)
    } else {
      await listenerHandle.remove()
    }
  } catch (error: unknown) {
    log.warn(`Native ${listenerName} listener could not be registered`, error)
  }
}

const handleBackButton = async (router: Router, event: BackButtonListenerEvent): Promise<void> => {
  const shouldUseDefaultHandling = window.dispatchEvent(
    new CustomEvent(NativeAppEventName.BackButton, {
      cancelable: true,
      detail: { canGoBack: event.canGoBack },
    })
  )

  if (!shouldUseDefaultHandling) {
    return
  }

  if (document.querySelector(VUETIFY_ACTIVE_OVERLAY_SELECTOR)) {
    window.dispatchEvent(
      new KeyboardEvent('keydown', {
        key: 'Escape',
        code: 'Escape',
        bubbles: true,
        cancelable: true,
      })
    )

    return
  }

  const activeDrawerScrim = document.querySelector<HTMLElement>(
    ACTIVE_TEMPORARY_DRAWER_SCRIM_SELECTOR
  )

  if (activeDrawerScrim) {
    activeDrawerScrim.click()

    return
  }

  const activeLayoutOverlay = document.querySelector<HTMLElement>(ACTIVE_LAYOUT_OVERLAY_SELECTOR)
  if (activeLayoutOverlay) {
    activeLayoutOverlay.click()

    return
  }

  if (event.canGoBack) {
    router.back()

    return
  }

  await App.minimizeApp()
}

export const synchronizeNativeSystemBars = async (themeName: NativeThemeName): Promise<void> => {
  if (!Capacitor.isNativePlatform()) {
    return
  }

  try {
    await SystemBars.setStyle({
      style: themeName === 'dark' ? SystemBarsStyle.Dark : SystemBarsStyle.Light,
    })
  } catch (error: unknown) {
    log.error('Native system bar theme synchronization failed', error)
  }
}

const handleRegistration = (token: Token): void => {
  dispatchNativeEvent(NativeAppEventName.PushRegistration, {
    platform: Capacitor.getPlatform(),
    token: token.value,
  })
  log.info('Native push registration completed')
}

const handleRegistrationError = (error: RegistrationError): void => {
  const message = getErrorMessage(error.error)

  dispatchNativeEvent(NativeAppEventName.PushRegistrationError, { message })
  log.error('Native push registration failed', message)

  void showNativeAlert({
    title: 'Notifications unavailable',
    message: 'This device could not register for push notifications. Please try again later.',
  })
}

const synchronizePushRegistration = async (requestPermission: boolean): Promise<void> => {
  let permission = await PushNotifications.checkPermissions()

  if (
    requestPermission &&
    (permission.receive === 'prompt' || permission.receive === 'prompt-with-rationale')
  ) {
    const shouldRequestPermission = await showNativeConfirm({
      title: 'Enable notifications?',
      message: 'Allow Vortex to alert you about activity that needs your attention.',
      okButtonTitle: 'Continue',
      cancelButtonTitle: 'Not now',
    })

    if (shouldRequestPermission) {
      permission = await PushNotifications.requestPermissions()
    }
  }

  dispatchNativeEvent(NativeAppEventName.PushPermissionChanged, {
    receive: permission.receive,
  })

  if (permission.receive === 'granted') {
    await PushNotifications.register()
  } else {
    log.warn('Native push permission was not granted')
  }
}

const registerAppListeners = async (router: Router, generation: number): Promise<void> => {
  await registerListener('URL', generation, () =>
    App.addListener('appUrlOpen', event => {
      void runNativeTask(
        'app URL navigation',
        () => navigateToNativeRoute(router, event.url, 'app-url'),
        generation
      )
    })
  )
  await registerListener('back button', generation, () =>
    App.addListener('backButton', event => {
      void runNativeTask('back button handling', () => handleBackButton(router, event), generation)
    })
  )
  await registerListener('pause', generation, () =>
    App.addListener('pause', () => {
      dispatchNativeEvent(NativeAppEventName.AppPaused, {})
    })
  )
  await registerListener('resume', generation, () =>
    App.addListener('resume', () => {
      dispatchNativeEvent(NativeAppEventName.AppResumed, {})
      void runNativeTask(
        'push permission refresh',
        () => synchronizePushRegistration(false),
        generation
      )
    })
  )
}

const registerPushListeners = async (router: Router, generation: number): Promise<void> => {
  await registerListener('push registration', generation, () =>
    PushNotifications.addListener('registration', handleRegistration)
  )
  await registerListener('push registration error', generation, () =>
    PushNotifications.addListener('registrationError', handleRegistrationError)
  )
  await registerListener('push receipt', generation, () =>
    PushNotifications.addListener('pushNotificationReceived', notification => {
      dispatchNativeEvent(NativeAppEventName.PushNotificationReceived, { notification })
    })
  )
  await registerListener('push action', generation, () =>
    PushNotifications.addListener('pushNotificationActionPerformed', (action: ActionPerformed) => {
      dispatchNativeEvent(NativeAppEventName.PushNotificationOpened, {
        notification: action.notification,
      })

      const route = resolvePushNotificationRoute(action.notification)
      if (route) {
        void runNativeTask(
          'push notification navigation',
          () => navigateToNativeRoute(router, route, 'push-notification'),
          generation
        )
      }
    })
  )
}

const configurePushNotifications = async (): Promise<void> => {
  if (Capacitor.getPlatform() === 'android') {
    await PushNotifications.createChannel({
      id: NativePushChannel.General,
      name: 'General notifications',
      description: 'General Vortex activity and account notifications',
      importance: 3,
      visibility: 0,
      vibration: true,
    })
  }

  await synchronizePushRegistration(true)
}

const handleColdStartUrl = async (router: Router): Promise<void> => {
  const launchUrl = await App.getLaunchUrl()
  if (launchUrl?.url) {
    await navigateToNativeRoute(router, launchUrl.url, 'launch-url')
  }
}

const initializeNativeRuntime = async (router: Router, generation: number): Promise<void> => {
  await registerAppListeners(router, generation)
  await registerPushListeners(router, generation)

  await runNativeTask('router readiness', () => router.isReady(), generation)
  await runNativeTask('cold-start navigation', () => handleColdStartUrl(router), generation)
  await runNativeTask('splash hide', () => SplashScreen.hide(), generation)
  await runNativeTask('push setup', () => configurePushNotifications(), generation)
}

export const initializeNativeApp = (router: Router): Promise<void> => {
  if (!Capacitor.isNativePlatform()) {
    return Promise.resolve()
  }

  if (!initializationPromise) {
    const generation = ++runtimeGeneration

    initializationPromise = initializeNativeRuntime(router, generation)
  }

  return initializationPromise
}

export const disposeNativeApp = async (): Promise<void> => {
  runtimeGeneration += 1
  await Promise.allSettled(listenerHandles.splice(0).map(listenerHandle => listenerHandle.remove()))
  initializationPromise = null
}
